/**
 * ATL-930 — the money to refund for one returned cart line, and the quantity rule that bounds it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS FOR
 *
 * `ReturnSaleScreen` RECOMPUTED the refund from the product's list price and then re-applied the two
 * discounts it happened to know about:
 *
 *     amount = salePrices[sale.store] * returnedQuantity
 *     if (itemDiscount) amount = amount * (100 - itemDiscount.percentage) / 100
 *     if (sale.discount) amount = amount * (100 - sale.discount) / 100
 *
 * What was actually charged is `finalItemPrice()` (utils/cartPricing.js), and it folds in three more
 * things that recomputation cannot see: the sale's price TYPE (`getProductPrice`), the purchase
 * method's `priceModificators`, and whatever price ended up on the line. **A charged amount cannot
 * be re-derived from a list price. It can only be read back.**
 *
 * Measured on prod 2026-08-23 over 1,141 returned lines (2HAPPY, HIM.HOUSE ASTANA, ИП Тажбенова):
 * the recompute disagreed with `finalItemPrice` on **620 of them — 54%**, always upward. Fleet-wide,
 * **126 sales across 15 orgs have been refunded more than they were charged: 1,477,511 ₸**, of which
 * 2HAPPY is 52 sales and 1,333,185 ₸.
 *
 *   • sale `ACq0I3JGqjhp8Iu4YWnn` — list 43,900, charged 35,120, refunded 43,900.
 *   • sale `OX7RMvgPpZtcM7eQ3Gl5` — charged 91,000, refunded 130,000.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE SECOND, QUIETER FAILURE: NaN
 *
 * The "Без скидки" (no discount) option is stored on some sales as `{amount: 0, text, title}` with
 * **no `percentage` key**. `100 - undefined` is NaN, so the line — and the whole
 * `totalReturnedAmount` — became NaN. `save()` gates on `totalReturnedAmount > 0`, which is false
 * for NaN, so the Return button silently did nothing. `itemPriceWithoutModifiers` in cartPricing.js
 * already carries the `|| amount || 0` fallback for exactly this shape; the return path never did.
 *
 * Not recomputing removes both failures at once, which is why the fix is a deletion rather than a
 * longer list of discount kinds to handle.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE QUANTITY RULE LIVES HERE TOO
 *
 * `returnedQuantity` is a free-text `decimal-pad` input and nothing bounded it. Two live lines prove
 * it (sale `p7Xf0Yx884QW3sPo7bbr`: 4 returned of 1 sold, and 24 of 12). Clamping only the MONEY
 * would be a half-fix that reads as a whole one: `saveSaleReturn` increments stock by
 * `returnedQuantity` directly (global/Firebase/firebase.ts:1811 and 1855), so an over-quantity
 * return corrupts the stock ledger on a path the money clamp cannot see. So the amount clamps (a
 * line is never worth more returned than it was sold for) AND the save refuses.
 */
import { chargedLineAmount, type CartLineAmountFields } from './cartLineAmount'

/** The subset of a returned cart line these rules read. Loose on purpose — both read paths feed it. */
export interface ReturnLineFields extends CartLineAmountFields {
  /** Free text off the screen's input, or a number once saved. */
  returnedQuantity?: number | string | null
}

/** `returnedQuantity` as a number, or `null` when it is not a usable one. */
export function parsedReturnedQuantity(item: ReturnLineFields | null | undefined): number | null {
  if (!item) return null
  const parsed = typeof item.returnedQuantity === 'number' ? item.returnedQuantity : parseFloat(String(item.returnedQuantity))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * ATL-959 (rounding) — the rounding convention a refund REQUEST is built with: `Math.round`,
 * applied PER LINE, never to the total.
 *
 * This is a named function with exactly one caller pattern on purpose. Both the amount a return
 * ASKS for and the ceiling that ADMITS it must round the same way, and the only structural way to
 * guarantee that is for both to go through this. See `fullLineRefund` below.
 *
 * `Math.round` (half up, towards +∞) is the convention already in the field, so it is preserved
 * rather than chosen: every `returns.total_amount` on prod was produced by it.
 */
function roundLineRefund(amount: number): number {
  return Math.round(amount)
}

/**
 * The money to refund for one returned line: the proportional share of what was CHARGED.
 *
 * The fraction is clamped to at most 1 — a line cannot be worth more returned than it was sold for.
 * A line sold with a zero or unusable quantity can only be returned whole, which is why the fraction
 * falls back to 1 rather than to 0: the two such lines on prod are both charged 0, so either answer
 * is 0 there, and for a charged line with no quantity the whole line is the only defensible number.
 */
export function returnedLineAmount(item: ReturnLineFields | null | undefined): number {
  if (!item) return 0
  const returnedQuantity = parsedReturnedQuantity(item)
  if (returnedQuantity === null || returnedQuantity <= 0) return 0
  const soldQuantity = typeof item.quantity === 'number' ? item.quantity : Number(item.quantity)
  const fraction =
    Number.isFinite(soldQuantity) && soldQuantity > 0
      ? Math.min(returnedQuantity / soldQuantity, 1)
      : 1
  return roundLineRefund(chargedLineAmount(item) * fraction)
}

/**
 * The most any return of this line can ask for — the whole line, rounded the way `returnedLineAmount`
 * rounds it.
 *
 * This is `returnedLineAmount` at `fraction = 1`, and it is the upper bound of `returnedLineAmount`
 * at EVERY fraction: `Math.round` is monotone non-decreasing and `fraction <= 1`, so
 * `round(a * f) <= round(a)` for any non-negative charged amount `a`. That inequality is the whole
 * reason `refundableSaleTotal` can be built from this — it makes "no legitimate single request can
 * exceed the basis" a property of the arithmetic rather than a hope about the data.
 */
export function fullLineRefund(item: CartLineAmountFields | null | undefined): number {
  return roundLineRefund(chargedLineAmount(item))
}

/**
 * Whether this line asks to return more than was sold — or an unreadable/negative quantity.
 *
 * `true` is a refusal, not a warning: the caller must not save. Stock and money both follow
 * `returnedQuantity`, and only one of the two is clamped.
 */
export function returnsMoreThanSold(item: ReturnLineFields | null | undefined): boolean {
  if (!item) return false
  const returnedQuantity = parsedReturnedQuantity(item)
  if (returnedQuantity === null || returnedQuantity < 0) return true
  const soldQuantity = typeof item.quantity === 'number' ? item.quantity : Number(item.quantity)
  return Number.isFinite(soldQuantity) && returnedQuantity > soldQuantity
}

/**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ATL-959 — THE CUMULATIVE RULE: a sale cannot be refunded more than it was CHARGED, IN TOTAL.
 *
 * `create_sale_return_atomic` has enforced this on the Supabase leg since ATL-573's "Fix G":
 *
 *     IF v_prior_returns + p_total_amount > COALESCE(final_total_cart_price, total_price, 0) + 0.01
 *       THEN RAISE EXCEPTION 'Cumulative refund exceeds sale total';
 *
 * **Nothing enforced it on the Firestore leg, and Firestore is the leg that commits first.**
 * `saveSaleReturn` writes a fresh `returns` document and overwrites `sale.returnInfo` with no read
 * of what came before, and this screen re-offers the entire cart on every visit. So a return
 * re-entered a second time produced TWO full refund documents in Firestore while Supabase correctly
 * refused the second — divergent books, and the till reported success because the dual-write is
 * non-fatal.
 *
 * Two live sales prove it (Sentry QARASAY-KASSA-2A6, `Cumulative refund exceeds sale total`):
 *   • `CB6GHF0ddCUUTjLJFHK7` — 3.00 charged; returns `vJlhlX1RkmAqdYrHhkFv` (201) and, 50s later,
 *     `L3ibqUpEXaimvUxUroqw` (refused by Supabase). Firestore holds both.
 *   • `fhpb0q4V5KL3ysMt9tFV` — same shape at 9.00.
 * Both are small. The same path on a large sale doubles a real refund.
 *
 * The rule below mirrors the RPC's 0.01 tolerance and comparison exactly, so the two legs agree
 * instead of one of them silently accepting what the other rejects. It applies to exchanges
 * (returnType 0) as well, because the RPC's cap does.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE BASIS — AND WHY THE RPC'S ORIGINAL ONE WAS WRONG
 *
 * The RPC bounded the refund by `COALESCE(final_total_cart_price, total_price, 0)`. The refund it
 * is bounding is built from something else entirely. `ReturnSaleScreen.initialReturnItems()`
 * pre-selects EVERY cart line at its full sold quantity, and `calculateTotalReturnedAmount()` sums
 * `returnedLineAmount()` — i.e. a full return requests exactly `Σ round(chargedLineAmount(cartItems))`,
 * rounded per line (see "AND THE LINE CANDIDATE IS ROUNDED" below).
 *
 * Those two numbers are NOT the same, and the difference is exact:
 *
 *     Σ finalItemPrice − final_total_cart_price = discount_amount + bonus_reduction + punch_card_reduction
 *
 * because `finalItemPrice` (utils/cartPricing.js) folds in the item discount, the price
 * modificators and the sale-level discount PERCENTAGE — its last line is
 * `price = price * (100 - discountPercentage) / 100` — but never the sale-level discount AMOUNT,
 * the bonus redemption or the punch-card reduction, all three of which `finalTotalCartPrice`
 * subtracts on top.
 *
 * Measured on prod 2026-08-25 over the 38,090 sales of the last 30 days:
 *   • Σ finalItemPrice == final_total_cart_price on 37,937; HIGHER on 153; LOWER on **0**.
 *   • the identity above holds to within 0.01 on 38,089 of 38,090.
 *   • 1,515 sales carry discount_percentage > 0 and 1,514 of them still have
 *     Σ finalItemPrice == final_total_cart_price exactly — the percentage really is already in the
 *     line. The 102 sales with a discount AMOUNT and the 50 with a bonus redemption are explained
 *     to the cent by that term alone (max |residual| 0.0000 and 0.0100).
 *   • delivery_price is NOT in the gap (23 sales carry one; all 23 are explained by the three
 *     terms without it).
 *
 * So on the old basis a full, legitimate return of any bonus-redeemed / amount-discounted /
 * punch-card sale exceeded the cap. Prod, all time: of the 2,042 sales carrying live returns, 132
 * are "over-refunded" against the old basis — but **32 of them have exactly ONE live return whose
 * amount is exactly Σ finalItemPrice**, i.e. one ordinary complete return, refused by arithmetic.
 * The other 100 never equal the line sum and stay refused (they are the ATL-930 recompute
 * over-refunds and the double submits). In the last 90 days, 408 sales across 30 of 188 orgs
 * cannot be fully returned on the Supabase leg today (median denied headroom 302.95).
 *
 * That was survivable only because the Supabase dual-write is non-fatal: the till completed the
 * return and the mirror diverged. Mirroring the same wrong basis here — on the leg that commits
 * FIRST and throws before it commits — would have converted a silent divergence into a cashier
 * facing a refused refund of real money.
 *
 * The basis is therefore the LARGER of the two candidates (see `refundableSaleTotal`). Where they
 * disagree the guard must be permissive, because the defect it exists to catch is a DUPLICATE full
 * refund, which roughly doubles the amount and so clears every candidate basis by a mile: sales
 * `CB6GHF0ddCUUTjLJFHK7` (3.00) and `fhpb0q4V5KL3ysMt9tFV` (9.00) have line sum == charged total
 * == 3.00 / 9.00, so a second full refund still asks for 2× and is still refused.
 *
 * ───────────────────────────────────────────────────────────────────────────────────────────────────
 * AND THE LINE CANDIDATE IS ROUNDED, BECAUSE THE REQUEST IS ROUNDED — PER LINE
 *
 * The first version of this rule summed `chargedLineAmount` UNROUNDED. That is not the number a
 * full return asks for. `returnedLineAmount` ends in `Math.round(...)` and
 * `calculateTotalReturnedAmount()` SUMS those, so the request is `Σ round(finalItemPriceᵢ)` — a
 * per-line-rounded number measured against an unrounded ceiling. Each line may round UP by as much
 * as 0.5, against a tolerance of 0.01.
 *
 * That is a FALSE REFUSAL AT THE TILL, and it is why this is a correction to the BASIS rather than
 * a wider tolerance: widening 0.01 to absorb the rounding would be a magic number hiding a basis
 * mismatch, and it would have to grow with the line count to keep working.
 *
 * Prod sale `OKtOKPKAZAWb9QILgZhc` is the shape: `final_total_cart_price` 167.01,
 * `bonus_reduction` 118.60, two lines worth 0 and 285.60. A full return asks
 * `round(0) + round(285.60) = 286`. The unrounded basis is `max(167.01, 285.60) = 285.60`, and
 * `0 + 286 > 285.60 + 0.01`, so the Firestore leg would THROW before `batch.commit()` — a refund
 * that has always committed, refused outright.
 *
 * Measured on prod 2026-08-25 (read-only), taking `Σ round(line)` as the ask:
 *   • last 30 days — **453 of 37,260 sales across 17 orgs** are refused a full return by the
 *     unrounded basis. Last 90 days: **1,482 of 111,776 across 36 orgs**. On the rounded basis both
 *     numbers are **0**.
 *   • nothing is newly refused in exchange: of the 2,044 sales carrying live returns, **0** move
 *     from within the old basis to outside the new one. The rounded candidate is smaller than the
 *     raw one on 82 sales in 90 days and by at most 0.30 — headroom no single request can reach.
 *   • the guard keeps catching what it is for: 100 sales exceed the old basis, **94 still exceed
 *     the new one** (minimum excess 13.00, mean 15,694). The 6 that drop out are each ONE live
 *     return equal to `Σ round(line)` — an ordinary complete return, e.g. `cKTYzZTB83Bf6fVJOesv`
 *     (declared 1,437.60, refunded 1,438.00) — i.e. exactly the false refusals.
 *
 * ───────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT PER-LINE ROUNDING CAN AND CANNOT DO ACROSS SEVERAL RETURNS
 *
 * ONE request is bounded, and no data is needed for that to hold. `Math.round` is monotone and
 * every fraction is ≤ 1, so any single request — full or partial, any subset of lines — is bounded
 * by `Σ round(line)` (see `fullLineRefund`). It is a property of the arithmetic.
 *
 * A SEQUENCE of partial returns is NOT bounded, and **the test for it is not whether the LINE
 * amount is fractional** — an earlier version of this comment said it was, and that is wrong.
 * Each request rounds independently, so the quantity that has to be an integer for the parts to
 * re-sum to the whole is the PER-UNIT amount `L / q` (the line total over the sold quantity), not
 * `L`. When `L / q` is an integer every partial ask `L × (r / q)` is an integer for integer `r`,
 * `Math.round` is a no-op, and the parts sum to exactly `L = round(L)`. When `L / q` is fractional
 * they need not.
 *
 * So the counterexample carries an INTEGER line amount — the shape the old wording called safe.
 * Prod sale `AAHMZqSncfZaUcNDpzvD`: one line, price 1.50 × quantity 2, `finalItemPrice` 3, declared
 * 3.00, so the basis is `max(3.00, round(3)) = 3`. Returned one unit at a time each leg asks
 * `round(3 × 1/2) = round(1.5) = 2`. The first commits (`0 + 2 ≤ 3.01`) and over-refunds that unit
 * by 0.50; the second is refused (`2 + 2 > 3.01`). The customer is refunded 2 of the 3 they were
 * charged, and no further PARTIAL request can finish the job — the remaining unit still asks 2
 * against 1 of headroom. The way out is the one the refusal message already names: delete the first
 * return and re-enter it whole, which asks `round(3 × 2/2) = 3` and passes.
 *
 * Measured on prod 2026-08-25 (read-only), one snapshot of the last 90 days — 111,823 live sales,
 * 218,122 cart lines:
 *   • **790 lines across 297 sales** have an integer sold quantity and `q × round(L/q) > round(L)`.
 *     **463 of those 790 have an integer line amount**, and **0 of the 790 have an integer `L/q`**
 *     — the criterion is `L/q`, exactly and only. (1,810 lines once fractional sold quantities are
 *     counted too.)
 *   • at sale level, returning every line one unit at a time breaches the cap on **292 of 111,823
 *     sales (0.26%) across 15 orgs**, by at most **29** currency units (mean 3.76). The unit split
 *     is not always the worst split: maximised over EVERY integer split it is **308 sales (0.28%)
 *     across 16 orgs**, and the ceiling is still 29 (worst `0jOKa9m3hZdegnShNTdV`: 23 lines, basis
 *     399,010, maximum ask 399,039).
 *   • nothing is exposed today — all 15 sales that carry more than one live return have zero
 *     accumulating lines. That is a fact about 15 sales, not a property of the arithmetic, and it
 *     is not why the cap is safe.
 *
 * REFUSING REMAINS THE CONSERVATIVE, CORRECT ANSWER. The excess is rounding-sized and bounded — at
 * most 0.5 per line per request — while the defect the cap exists for is a duplicate full refund at
 * ~2×. Absorbing it would need a tolerance scaling with both the line count and the number of
 * returns, which is a cap that has stopped being one. The price is a refusal a cashier can actually
 * hit; it is recorded as such in `docs/adr/ADR-001-money-representation-and-rounding.md`.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */

/** A previously-saved return on the same sale, as the `returns` collection stores it. */
export interface PriorReturnFields {
  totalReturnedAmount?: number | string | null
}

/** The subset of a sale document the refundable-total rule reads. */
export interface RefundableSaleFields {
  finalTotalCartPrice?: number | null
  totalPrice?: number | null
  cartItems?: CartLineAmountFields[] | null
}

/** A finite number, or `null` when the value cannot be read as one. `0` is a real value. */
function finiteOrNull(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

/**
 * The ceiling on what a sale may be refunded, in total — the basis the cumulative cap measures
 * against. The LARGER of two candidates, never one of them alone:
 *
 *   • the DECLARED total — `finalTotalCartPrice`, else `totalPrice`. What the customer paid in
 *     money, and the RPC's original `COALESCE(final_total_cart_price, total_price, 0)`.
 *   • the CHARGED LINE TOTAL — `Σ fullLineRefund(cartItems)`, i.e. `Σ round(chargedLineAmount)`.
 *     What the return screen actually asks for when every line is returned whole, by construction,
 *     ROUNDED PER LINE exactly as `returnedLineAmount` rounds it. Summing the charged amounts
 *     unrounded and comparing them against a per-line-rounded request is the basis mismatch
 *     documented at length above.
 *
 * Taking the larger is not laxity, it is the only defensible reading when the two disagree (see the
 * measurements in the block above):
 *
 *   • the line total EXCEEDS the declared one on every loyalty-bonus, amount-discount and
 *     punch-card sale — 408 sales across 30 orgs in the last 90 days. Bounding by the declared
 *     total refuses a complete, correct return of each of them.
 *   • the declared total exceeds the line total on a vanishing tail (1 sale in a 6,363-sale random
 *     sample of pre-2026-07 sales; 0 of the 2,049 sales that actually carry returns) — a sale whose
 *     cart snapshot understates it. Bounding by the line total alone would refuse those.
 *   • a sale can carry a declared total that is simply STALE relative to its own lines: prod sale
 *     `hfozwdKuymUeMkffcJ6w` has final_total_cart_price 5,260.00 with lines worth 5,960.00 and
 *     every discount field zero. Its single 5,960.00 return is refused by the declared total and
 *     allowed — correctly — by the line total.
 *
 * Neither candidate can be discarded, and above the larger of them nothing legitimate exists: a
 * duplicate refund, the defect this guard is for, is ~2× and clears both.
 *
 * The RPC's final `0` is NOT mirrored. Falling back to 0 would turn a sale document missing both
 * totals into a sale that can never be refunded at all — a false refusal of real money. `null`
 * means "no basis at all" (no totals AND no cart lines) and the caller must then not refuse. Prod
 * carries zero such sales (0 of a 24,183-row sample, 0 of the 2,049 with returns), so this is a
 * guard against a shape that does not exist rather than a licence for one that does.
 */
export function refundableSaleTotal(sale: RefundableSaleFields | null | undefined): number | null {
  if (!sale) return null
  const candidates: number[] = []
  // `??` and not `||`: a declared total of 0 is a real value (a fully bonus-funded sale) and must
  // not fall through to `totalPrice`.
  const declared = finiteOrNull(sale.finalTotalCartPrice) ?? finiteOrNull(sale.totalPrice)
  if (declared !== null) candidates.push(declared)
  if (Array.isArray(sale.cartItems) && sale.cartItems.length > 0) {
    // `fullLineRefund`, not `chargedLineAmount`: the request this bounds is rounded PER LINE, so
    // the ceiling has to be too. See "AND THE LINE CANDIDATE IS ROUNDED" above.
    candidates.push(sale.cartItems.reduce((sum, line) => sum + fullLineRefund(line), 0))
  }
  if (candidates.length === 0) return null
  return Math.max(...candidates)
}

/**
 * The money already refunded against a sale by the returns that still EXIST.
 *
 * Firestore hard-deletes a return document (`deleteSaleReturn` → `batch.delete(returnRef)`), so the
 * documents this is handed are by construction the live ones — the same set Supabase means by
 * `deleted_at IS NULL`. A return whose amount is unreadable contributes 0 rather than NaN: NaN would
 * poison the comparison into always-false and silently disable the whole rule.
 */
export function priorReturnedTotal(priorReturns: PriorReturnFields[] | null | undefined): number {
  if (!Array.isArray(priorReturns)) return 0
  return priorReturns.reduce((sum, prior) => {
    const amount =
      typeof prior?.totalReturnedAmount === 'number'
        ? prior.totalReturnedAmount
        : parseFloat(String(prior?.totalReturnedAmount))
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)
}

/**
 * Whether this return, added to the ones already on the sale, would exceed the sale's refundable
 * total.
 *
 * `true` is a refusal, not a warning: the caller must not commit. `refundableTotal === null` means
 * the sale carries no readable basis, and an unmeasurable bound is not a breached one — it returns
 * `false` and leaves the RPC's own race-safe cap as the enforcement of last resort.
 *
 * The 0.01 tolerance is the RPC's, kept identical so a full refund that passes one leg passes both.
 */
export function cumulativeRefundExceedsSale(args: {
  refundableTotal: number | null
  priorReturnedTotal: number
  totalReturnedAmount: number
}): boolean {
  const { refundableTotal, priorReturnedTotal: prior, totalReturnedAmount } = args
  if (refundableTotal === null) return false
  if (!Number.isFinite(totalReturnedAmount) || !Number.isFinite(prior)) return false
  return prior + totalReturnedAmount > refundableTotal + 0.01
}

/**
 * The `name` a cumulative-refund refusal carries.
 *
 * Identity is checked by NAME, not `instanceof`. Babel's transpilation of `class X extends Error`
 * for the RN/Hermes targets does not reliably preserve the prototype chain, so `instanceof` can be
 * false for an error this module itself threw — and the consequence of that particular
 * false-negative is a correct business refusal being reported to Sentry as a crash.
 */
export const CUMULATIVE_REFUND_EXCEEDED = 'CumulativeRefundExceededError'

/** The numbers behind a refusal, so a caller can re-render them without re-deriving them. */
export interface CumulativeRefundRefusal {
  refundableTotal: number
  priorReturnedTotal: number
  requestedRefund: number
}

/** Builds the refusal `saveSaleReturn` throws. `message` is what the cashier reads. */
export function cumulativeRefundExceededError(
  message: string,
  details: CumulativeRefundRefusal,
): Error & CumulativeRefundRefusal {
  const error = new Error(message) as Error & CumulativeRefundRefusal
  error.name = CUMULATIVE_REFUND_EXCEEDED
  error.refundableTotal = details.refundableTotal
  error.priorReturnedTotal = details.priorReturnedTotal
  error.requestedRefund = details.requestedRefund
  return error
}

/**
 * Whether a caught error is the cumulative-refund refusal.
 *
 * Callers use this to keep it out of the CRASH tracker: it is a business rule working, not a
 * fault, and a refusal that filled the crash cluster would bury the failures that are faults.
 * It is NOT a reason to drop the event entirely — see `asCumulativeRefundRefusal`.
 */
export function isCumulativeRefundExceededError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === CUMULATIVE_REFUND_EXCEEDED
  )
}

/**
 * The refusal's numbers when `error` is one, else `null`.
 *
 * This exists so the refusal can be REPORTED rather than suppressed. A refusal is a correct
 * business outcome, but it is also real money refused at the counter, and the number it is measured
 * against (`refundableSaleTotal`) is exactly what was wrong before this ticket. Reporting it as a
 * `warning` carrying these three values is what makes a future wrong basis visible instead of
 * silent; a refusal whose `requestedRefund` sits at or under `refundableTotal - priorReturnedTotal`
 * is by definition a bug in the rule.
 *
 * The three fields are re-checked rather than trusted: a foreign error could carry the same `name`,
 * and reporting `undefined` as a money figure teaches the reader nothing.
 */
export function asCumulativeRefundRefusal(error: unknown): CumulativeRefundRefusal | null {
  if (!isCumulativeRefundExceededError(error)) return null
  const candidate = error as Partial<CumulativeRefundRefusal>
  const { refundableTotal, priorReturnedTotal: prior, requestedRefund } = candidate
  if (
    typeof refundableTotal !== 'number' ||
    typeof prior !== 'number' ||
    typeof requestedRefund !== 'number'
  ) {
    return null
  }
  return { refundableTotal, priorReturnedTotal: prior, requestedRefund }
}
